/**
 * The routes themselves.
 *
 * Three kinds, and the ordering matters:
 *
 *   PATTERNING MODULES  four ways to put the same pitch on a wafer. This is the
 *                       comparison that decided the last decade of the
 *                       industry, and it is arithmetic over a route.
 *
 *   LAYER MODULES       a gate layer and a metal layer. What a wafer does
 *                       between one lithography step and the next.
 *
 *   PRODUCT ROUTE       the whole thing. Metal layers repeat, so the wafer goes
 *                       round the same loop ten times, and the step count and
 *                       mask count come out where a real logic process sits.
 *
 * Every step carries a throughput, and cost falls out of that against the
 * capital rate of the tool. Nothing here is priced by hand.
 */

import type { FlowStep, ProcessFlow } from './model'

// Shorthand, so a route reads as a route rather than as object literals.
const step = (
  tool: string,
  label: string,
  purpose: string,
  wafersPerHour: number,
  extra: Partial<FlowStep> = {},
): FlowStep => ({ tool, label, purpose, wafersPerHour, ...extra })

/** Particles a routine operation adds. Small, and there are hundreds of them. */
const TOUCH = 0.0015
/** A wet or CMP step is dirtier than a vacuum one. */
const WET_TOUCH = 0.004
/** Baseline chance of losing a wafer at any given operation. */
const SCRAP = 0.00012

// ===========================================================================
// PATTERNING MODULES — four ways to the same pitch
// ===========================================================================

/**
 * Single exposure on an immersion scanner.
 *
 * The reference, and it cannot reach the pitch the others can - it is here so
 * the comparison has a floor and so the k1 limit is visible as a route that
 * simply does not have the option.
 */
export const SINGLE_DUV: ProcessFlow = {
  id: 'single-duv',
  name: 'Single exposure, 193i',
  purpose: 'One mask, two operations, and a hard stop at 36 nm half-pitch.',
  nodes: [
    step('LITH-03', 'Expose', 'Print the pattern directly', 275, {
      mask: true,
      particleAddersCm2: TOUCH,
      scrapRisk: SCRAP,
    }),
    step('ETCH-01', 'Pattern transfer etch', 'Transfer resist into the film', 40, {
      particleAddersCm2: TOUCH,
      scrapRisk: SCRAP,
    }),
  ],
}

/**
 * Single exposure on EUV.
 *
 * The same two operations, on a scanner that costs two and a quarter times as
 * much and runs slower. Everything about the EUV argument is in those two
 * sentences.
 */
export const SINGLE_EUV: ProcessFlow = {
  id: 'single-euv',
  name: 'Single exposure, EUV',
  purpose: 'One mask, two operations, and the pitch the others need eight steps for.',
  nodes: [
    step('EUV-22', 'Expose', 'Print the pattern directly at 13.5 nm', 160, {
      mask: true,
      particleAddersCm2: TOUCH,
      scrapRisk: SCRAP,
    }),
    step('ETCH-01', 'Pattern transfer etch', 'Transfer resist into the film', 40, {
      particleAddersCm2: TOUCH,
      scrapRisk: SCRAP,
    }),
  ],
}

/**
 * Litho-etch-litho-etch.
 *
 * The obvious way to double a pitch: print half the lines, etch them, print the
 * other half in between, etch again. It works, and it costs a SECOND MASK - and
 * the overlay between the two exposures becomes a CD error, because a line that
 * lands off-centre is a line with the wrong space on each side.
 */
export const LELE: ProcessFlow = {
  id: 'lele',
  name: 'LELE double patterning',
  purpose: 'Two masks, four operations. The overlay between them becomes a CD error.',
  nodes: [
    step('LITH-03', 'Expose, first population', 'Print every other line', 275, {
      mask: true,
      particleAddersCm2: TOUCH,
      scrapRisk: SCRAP,
    }),
    step('ETCH-01', 'Etch, first population', 'Transfer it', 40, {
      particleAddersCm2: TOUCH,
      scrapRisk: SCRAP,
    }),
    step('LITH-03', 'Expose, second population', 'Print the lines in between', 275, {
      mask: true,
      particleAddersCm2: TOUCH,
      scrapRisk: SCRAP,
    }),
    step('ETCH-01', 'Etch, second population', 'Transfer that too', 40, {
      particleAddersCm2: TOUCH,
      scrapRisk: SCRAP,
    }),
  ],
}

/**
 * Self-aligned quadruple patterning.
 *
 * ONE mask and eight operations, and the two extra lines per mandrel are
 * self-aligned - there is no second exposure to misalign against, which is what
 * "self-aligned" means and why it beats LELE on overlay.
 *
 * This is also the route PAT-21 runs. The module used to carry its own copy of
 * these eight steps; now it reads them from here, so the panel and the route
 * cannot disagree about what the cell does.
 */
export const SAQP: ProcessFlow = {
  id: 'saqp',
  name: 'SAQP',
  purpose: 'One mask, eight operations, and a final pitch set by a film thickness.',
  nodes: [
    step('LITH-03', 'Print the mandrel', 'The only exposure in the whole module', 275, {
      mask: true,
      particleAddersCm2: TOUCH,
      scrapRisk: SCRAP,
    }),
    step('ETCH-01', 'Trim the mandrel', 'The knob that centres the pattern', 45, {
      particleAddersCm2: TOUCH,
      scrapRisk: SCRAP,
    }),
    step('DEP-02', 'Deposit spacer 1', 'ALD — this thickness sets the half pitch', 25, {
      particleAddersCm2: TOUCH,
      scrapRisk: SCRAP,
    }),
    step('ETCH-01', 'Spacer 1 etch', 'Anisotropic: clears the horizontals', 40, {
      particleAddersCm2: TOUCH,
      scrapRisk: SCRAP,
    }),
    step('ETCH-01', 'Pull the mandrel', 'Two lines where there was one', 45, {
      particleAddersCm2: TOUCH,
      scrapRisk: SCRAP,
    }),
    step('DEP-02', 'Deposit spacer 2', 'The first spacers are now the mandrels', 25, {
      particleAddersCm2: TOUCH,
      scrapRisk: SCRAP,
    }),
    step('ETCH-01', 'Spacer 2 etch', 'Anisotropic again', 40, {
      particleAddersCm2: TOUCH,
      scrapRisk: SCRAP,
    }),
    step('ETCH-01', 'Pull the first spacers', 'Four lines where there was one', 45, {
      particleAddersCm2: TOUCH,
      scrapRisk: SCRAP,
    }),
  ],
}

/**
 * Quadruple patterning by repeated litho-etch.
 *
 * The like-for-like comparison against SAQP: the same four-fold pitch
 * division, reached by printing four separate populations. It is the route
 * nobody runs, and this is where you find out why - four masks, and three
 * overlay budgets between exposures that all land as CD error.
 */
export const LE4: ProcessFlow = {
  id: 'le4',
  name: 'LE⁴ quadruple patterning',
  purpose: 'Four masks to the same pitch SAQP reaches with one.',
  nodes: [1, 2, 3, 4].flatMap((n) => [
    step('LITH-03', 'Expose, population ' + n, 'Print one line in four', 275, {
      mask: true,
      particleAddersCm2: TOUCH,
      scrapRisk: SCRAP,
    }),
    step('ETCH-01', 'Etch, population ' + n, 'Transfer it', 40, {
      particleAddersCm2: TOUCH,
      scrapRisk: SCRAP,
    }),
  ]),
}

/**
 * The patterning family, in the order a student should meet it: one mask,
 * one mask at a shorter wavelength, then the three ways of beating the pitch
 * without one.
 *
 * THE ONE LIST. This used to be written out at three call sites - here, the
 * route explorer, and route-check - which meant adding a sixth route took
 * three edits and the harness could quietly not cover it. A fourth list,
 * ALL_ROUTES, had already drifted: it was missing LE4 and nothing read it.
 * Both consumers now import this, so coverage follows the list by
 * construction rather than by everyone remembering.
 */
export const PATTERNING_ROUTES = [SINGLE_DUV, SINGLE_EUV, LELE, LE4, SAQP]

// ===========================================================================
// LAYER MODULES
// ===========================================================================

/** A CMOS gate layer, patterned however the caller chooses. */
export function gateModule(patterning: ProcessFlow): ProcessFlow {
  return {
    id: 'gate-' + patterning.id,
    name: 'Gate layer — ' + patterning.name,
    purpose: 'Well, gate oxide, polysilicon, pattern, and the source/drain that follows it.',
    nodes: [
      step('CLN-09', 'Pre-gate clean', 'A furnace drives contamination in, it does not remove it', 60, {
        particleAddersCm2: WET_TOUCH,
        scrapRisk: SCRAP,
      }),
      step('IMP-10', 'Well implant', 'The tub the device sits in', 55, {
        mask: true,
        particleAddersCm2: TOUCH,
        scrapRisk: SCRAP,
      }),
      step('THRM-04', 'Well drive-in', 'Deep, and expensive in budget', 12, {
        thermalSeconds: 1800,
        particleAddersCm2: TOUCH,
        scrapRisk: SCRAP,
      }),
      step('THRM-04', 'Gate oxidation', 'The thinnest, most consequential film in the device', 15, {
        thermalSeconds: 240,
        particleAddersCm2: TOUCH,
        scrapRisk: SCRAP,
      }),
      step('DEP-02', 'Polysilicon deposition', 'The gate electrode', 25, {
        particleAddersCm2: TOUCH,
        scrapRisk: SCRAP,
      }),
      { flow: patterning },
      step('IMP-10', 'Source/drain implant', 'Self-aligned to the gate that was just etched', 55, {
        mask: true,
        particleAddersCm2: TOUCH,
        scrapRisk: SCRAP,
      }),
      step('THRM-04', 'Spike anneal', 'Activate the dopant and move it as little as possible', 20, {
        thermalSeconds: 60,
        particleAddersCm2: TOUCH,
        scrapRisk: SCRAP,
      }),
      step('MET-05', 'Post-gate metrology', 'Because everything after this assumes it worked', 90, {
        particleAddersCm2: 0,
      }),
    ],
  }
}

/**
 * One damascene metal layer.
 *
 * Copper cannot be plasma etched, so the whole scheme is inverted: etch the
 * dielectric, fill it with metal, take the excess off. ECD-12 and CMP-11 are
 * two halves of one operation and they appear here as consecutive steps for
 * exactly that reason.
 */
export const METAL_LAYER: ProcessFlow = {
  id: 'metal-layer',
  name: 'Damascene metal layer',
  purpose: 'Etch the dielectric, fill it with copper, take the excess off.',
  nodes: [
    step('DEP-02', 'Dielectric deposition', 'The insulator the trenches go into', 25, {
      particleAddersCm2: TOUCH,
      scrapRisk: SCRAP,
    }),
    step('LITH-03', 'Trench lithography', 'Where the wires go', 275, {
      mask: true,
      particleAddersCm2: TOUCH,
      scrapRisk: SCRAP,
    }),
    step('ETCH-01', 'Via etch', 'Down to the layer below', 40, {
      particleAddersCm2: TOUCH,
      scrapRisk: SCRAP,
    }),
    step('ETCH-01', 'Trench etch', 'And the wire that sits on top of the via', 40, {
      particleAddersCm2: TOUCH,
      scrapRisk: SCRAP,
    }),
    step('ETCH-01', 'Resist strip', 'Ash the resist off before anything is deposited', 55, {
      particleAddersCm2: TOUCH,
      scrapRisk: SCRAP,
    }),
    step('CLN-09', 'Post-etch clean', 'Polymer off, before anything is deposited on it', 60, {
      particleAddersCm2: WET_TOUCH,
      scrapRisk: SCRAP,
    }),
    step('DEP-02', 'Barrier and seed', 'Copper poisons silicon, so it is fenced in', 22, {
      particleAddersCm2: TOUCH,
      scrapRisk: SCRAP,
    }),
    step('ECD-12', 'Copper plate', 'Bottom-up fill, and an overburden for CMP', 30, {
      particleAddersCm2: WET_TOUCH,
      scrapRisk: SCRAP,
    }),
    step('CMP-11', 'Copper CMP', 'Take off everything above the trench', 35, {
      particleAddersCm2: WET_TOUCH,
      scrapRisk: SCRAP * 2,
    }),
    step('CLN-09', 'Post-CMP clean', 'Slurry is a defect if it is left on', 60, {
      particleAddersCm2: WET_TOUCH,
      scrapRisk: SCRAP,
    }),
    step('MET-05', 'Thickness and dishing check', 'The next layer prints on this surface', 90, {
      particleAddersCm2: 0,
    }),
  ],
}

// ===========================================================================
// PRODUCT ROUTE
// ===========================================================================

/** Substrate: an ingot becomes a wafer. */
export const SUBSTRATE: ProcessFlow = {
  id: 'substrate',
  name: 'Substrate',
  purpose: 'Polysilicon and a seed become a polished wafer.',
  nodes: [
    // Seventy-two hours for one ingot, and one ingot is about a thousand
    // wafers - so the per-wafer figure is fourteen an hour, not one every
    // thirty hours. Getting this wrong made the substrate 95% of the cost of
    // a finished wafer, which is off by about two orders of magnitude.
    step('CZ-07', 'Crystal growth', '72 hours for one ingot, and one ingot is ~1000 wafers', 14, {
      scrapRisk: 0.002,
    }),
    step('WPR-08', 'Slice, lap, etch, polish', 'One crystal becomes a thousand wafers', 25, {
      particleAddersCm2: WET_TOUCH,
      scrapRisk: 0.004,
    }),
    step('CLN-09', 'Incoming clean', 'Before it is allowed into the front end', 60, {
      particleAddersCm2: WET_TOUCH,
      scrapRisk: SCRAP,
    }),
    step('EPI-20', 'Epitaxial device layer', 'A defect-free, doped-to-order layer over the bulk', 30, {
      particleAddersCm2: TOUCH,
      scrapRisk: 0.0008,
    }),
  ],
}

/** Back end: a wafer becomes packaged, tested parts. */
export const BACK_END: ProcessFlow = {
  id: 'back-end',
  name: 'Assembly and test',
  purpose: 'A finished wafer becomes binned parts.',
  nodes: [
    step('INSP-13', 'Final inspection', 'Last look before the wafer is cut up', 45, {}),
    step('PRB-14', 'Wafer sort', 'The first honest yield number anyone has had', 3, {
      scrapRisk: SCRAP,
    }),
    step('DICE-15', 'Grind and dice', 'And the wafer stops being a wafer', 20, {
      scrapRisk: 0.001,
    }),
    step('BOND-16', 'Die attach and wire bond', 'Onto a substrate', 30, { scrapRisk: 0.001 }),
    step('PKG-17', 'Mould and cure', 'And the die is under 183 MPa for the rest of its life', 40, {
      scrapRisk: 0.0008,
    }),
    step('FT-18', 'Final test and burn-in', 'Bin it, and decide what escapes', 25, {
      scrapRisk: 0.0005,
    }),
  ],
}

/**
 * EUV patterning as a fab actually runs it: expose, MEASURE, then etch.
 *
 * `SINGLE_EUV` above is two operations because it exists to be compared with
 * the other patterning modules, mask for mask. On a floor the exposure is not
 * trusted until a CD-SEM has looked at it - after develop, while a bad print
 * can still be stripped and reworked for the price of a resist coat - and at
 * EUV pitches that look is not a sample plan, it is a step. This is the
 * module the line simulation runs, and it is what puts SEM-23 on a route.
 */
export const EUV_ADI: ProcessFlow = {
  id: 'euv-adi',
  name: 'EUV exposure with after-develop inspection',
  purpose: 'One mask, three operations: nothing is etched until the CD-SEM has seen it.',
  nodes: [
    step('EUV-22', 'Expose', 'Print the pattern directly at 13.5 nm', 160, {
      mask: true,
      particleAddersCm2: TOUCH,
      scrapRisk: SCRAP,
    }),
    step('SEM-23', 'After-develop CD', 'The last look while a bad print can still be reworked', 60, {
      particleAddersCm2: 0,
    }),
    step('ETCH-01', 'Pattern transfer etch', 'Transfer resist into the film', 40, {
      particleAddersCm2: TOUCH,
      scrapRisk: SCRAP,
    }),
  ],
}

/** The damascene loop with its trench printed on EUV-22 and checked on SEM-23. */
const METAL_LAYER_EUV: ProcessFlow = {
  id: 'metal-layer-euv',
  name: 'Damascene metal layer, EUV',
  purpose: 'The tight-pitch lower metals: the same loop, printed at 13.5 nm and measured before it is etched.',
  nodes: METAL_LAYER.nodes.flatMap((n) =>
    'tool' in n && n.tool === 'LITH-03'
      ? [
          step('EUV-22', 'Trench lithography', 'Where the wires go', 160, { mask: true, particleAddersCm2: TOUCH, scrapRisk: SCRAP }),
          step('SEM-23', 'After-develop CD', 'The last look while a bad print can still be reworked', 60, { particleAddersCm2: 0 }),
        ]
      : [n],
  ),
}

/**
 * A leading-edge product: the gate and the lowest `euvLayers` metal layers
 * on EUV, the relaxed upper metals still on the immersion scanner. That split
 * is how EUV is actually deployed - a fab buys a few of the most expensive
 * tool there is and spends it only where the pitch demands it.
 */
export function euvProductRoute(euvLayers = 4, metalLayers = 10): ProcessFlow {
  const euv = Math.max(0, Math.min(euvLayers, metalLayers))
  return {
    id: 'product-euv',
    name: 'Full product route — EUV at the gate and lower metals',
    purpose: 'Silicon to binned part, with EUV where the pitch demands it and immersion everywhere else.',
    nodes: [
      { flow: SUBSTRATE },
      { flow: gateModule(EUV_ADI) },
      { flow: METAL_LAYER_EUV, repeat: euv },
      { flow: METAL_LAYER, repeat: metalLayers - euv },
      { flow: BACK_END },
    ],
  }
}

/**
 * A superconducting qubit chip: the same fab, a different product.
 *
 * Eight operations where the logic product has hundreds, on six tools the
 * logic route already uses and two it does not. It is here for what it says
 * about THOSE two. SEM-23 is on the critical path rather than a sample plan:
 * the junction's area sets E_J, E_J sets the qubit's frequency, and a junction
 * ten percent wide is a qubit that collides with its neighbour - so every
 * junction is measured. And QDT-24 is the slowest step on any route in this
 * file by two orders of magnitude, because a cooldown is a day and a fridge
 * holds a handful of chips: the test is the bottleneck, which is the opposite
 * of a logic fab and the reason quantum foundries buy fridges before they buy
 * anything else.
 *
 * The tunnel barrier is grown on DEP-02, the ALD tool, because aluminium
 * oxide by the cycle is exactly what that tool deposits. A production
 * junction oxidises its aluminium in place instead; the thickness control
 * argument - a tenth of an angstrom moves the qubit - is the same either way,
 * and it is QDT-24's own panel that makes it.
 */
export const QUANTUM_CHIP: ProcessFlow = {
  id: 'quantum-chip',
  name: 'Transmon test chip',
  purpose: 'Eight operations, one junction a nanometre thick, and a test that takes a day at 10 mK.',
  nodes: [
    step('CLN-09', 'Substrate clean', 'A lossy surface is a lossy qubit', 60, {
      particleAddersCm2: WET_TOUCH,
      scrapRisk: SCRAP,
    }),
    step('LITH-03', 'Expose the circuit', 'Capacitor pads, resonators and the junction leads', 275, {
      mask: true,
      particleAddersCm2: TOUCH,
      scrapRisk: SCRAP,
    }),
    step('ETCH-01', 'Etch the circuit', 'Transfer it into the superconducting film', 40, {
      particleAddersCm2: TOUCH,
      scrapRisk: SCRAP,
    }),
    step('DEP-02', 'Grow the tunnel barrier', 'A nanometre of aluminium oxide, counted in cycles', 12, {
      particleAddersCm2: TOUCH,
      scrapRisk: SCRAP,
    }),
    step('SEM-23', 'Measure every junction', 'Area sets the frequency, and ten percent wide is a collision', 6, {
      scrapRisk: SCRAP,
    }),
    step('DICE-15', 'Dice into chips', 'A fridge takes a chip, not a wafer', 20, { scrapRisk: 0.001 }),
    step('BOND-16', 'Wire bond to the sample holder', 'Aluminium wire, which goes superconducting with the chip', 30, {
      scrapRisk: 0.001,
    }),
    step('QDT-24', 'Cool down and characterise', 'A day per cooldown: the test is the bottleneck', 0.05, {
      scrapRisk: 0.002,
    }),
  ],
}

/**
 * The whole product.
 *
 * Ten metal layers, because that is what a logic process has, and the wafer
 * goes round the same loop ten times. The step count and mask count that come
 * out of this are not typed in anywhere - they are what the route adds up to.
 */
export function productRoute(patterning: ProcessFlow, metalLayers = 10): ProcessFlow {
  return {
    id: 'product-' + patterning.id,
    name: 'Full product route — ' + patterning.name,
    purpose: 'Silicon to binned part, with the interconnect loop run once per metal layer.',
    nodes: [
      { flow: SUBSTRATE },
      { flow: gateModule(patterning) },
      { flow: METAL_LAYER, repeat: metalLayers },
      { flow: BACK_END },
    ],
  }
}

// ALL_ROUTES used to sit here: a fourth list, of everything, read by nobody
// and already wrong (it had lost LE4). A catalogue that nothing consumes
// cannot be kept honest, and this one proved it. The route explorer builds
// its own groups from PATTERNING_ROUTES and the module constants above,
// which are the same pieces and are all exported.
